<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { vMaska } from 'maska';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    modelValue?: string | number | null;
    mask?: string | string[];
    editLabel?: string;
  }>(),
  {
    modelValue: '',
    editLabel: 'Editar',
  },
);

const emit = defineEmits<{
  (event: 'update:modelValue', value: string): void;
}>();

const input = ref<HTMLInputElement>();
const editing = ref(false);

const suppressed = computed(
  () => typeof props.modelValue === 'string' && props.modelValue.includes('*'),
);

const showSuppressedValue = computed(() => suppressed.value && !editing.value);

const value = computed({
  get: () => props.modelValue ?? '',
  set: (newValue) => emit('update:modelValue', String(newValue)),
});

watch(
  () => props.modelValue,
  (newValue) => {
    if (typeof newValue === 'string' && newValue.includes('*')) {
      editing.value = false;
    }
  },
);

const enableEditing = async () => {
  editing.value = true;
  emit('update:modelValue', '');
  await nextTick();
  input.value?.focus();
};
</script>

<template>
  <div class="sensitive-input">
    <input
      v-if="showSuppressedValue"
      class="form-input"
      type="text"
      disabled
      :value="modelValue"
      aria-label="Valor suprimido"
    />

    <input
      v-else-if="mask"
      ref="input"
      v-bind="$attrs"
      v-maska
      :data-maska="mask"
      class="form-input"
      v-model="value"
    />

    <input
      v-else
      ref="input"
      v-bind="$attrs"
      class="form-input"
      v-model="value"
    />

    <button
      v-if="showSuppressedValue"
      class="btn btn-primary sensitive-input__edit"
      type="button"
      @click="enableEditing"
    >
      {{ editLabel }}
    </button>
  </div>
</template>

<style lang="scss" scoped>
.sensitive-input {
  display: flex;
  gap: 0.4rem;
  align-items: center;

  .form-input {
    flex: 1;
  }

  &__edit {
    flex: 0 0 auto;
    white-space: nowrap;
  }
}
</style>
